"use client"
import React, { useRef, useState } from "react"
import { createPortal } from "react-dom"
import { Image as ImageIcon, Loader, Lock, Globe, Sparkles, Upload, X } from "lucide-react"

import { Input } from "../ui/input"
import { generateImageDataUrl } from "../../services/imageGeneration"
import ImageCropper from "./ImageCropper"

/**
 * Create an avatar with the little that is actually required to exist: a name,
 * and — while it is private — the project it belongs to. An image and a
 * description if you have them.
 *
 * Everything else (blocks, output, examples) is written afterwards, in the
 * avatar's own view, where it can be seen next to what it affects. The old
 * four-step wizard asked for all of it up front, before you had anywhere to
 * look, and then dropped you back into a list.
 */
export default function CreateAvatarDialog({
  submitLabel = "Create avatar",
  accountId,
  name,
  setName,
  description,
  setDescription,
  image,
  onImageChange,
  onImageError,
  isPublic,
  setIsPublic,
  projects,
  projectsError,
  selectedProjects,
  setSelectedProjects,
  validationErrors,
  clearValidationError,
  submitting,
  onCancel,
  onSubmit,
}) {
  const [pendingImage, setPendingImage] = useState(null)
  const [showPrompt, setShowPrompt] = useState(false)
  const [prompt, setPrompt] = useState("")
  const [generating, setGenerating] = useState(false)
  const [generateError, setGenerateError] = useState("")
  const fileRef = useRef(null)

  const projectId = selectedProjects[0] || ""
  const nameMissing = validationErrors?.name
  const projectMissing = validationErrors?.projects

  const pickFile = (e) => {
    const file = e.target.files[0]
    e.target.value = ""
    if (!file) return
    if (!file.type.startsWith("image/")) return onImageError("Please select an image file")
    if (file.size > 10 * 1024 * 1024) return onImageError("File size must be less than 10MB")
    setPendingImage(file)
  }

  const handleGenerate = async () => {
    setGenerating(true)
    setGenerateError("")
    try {
      // No project: the avatar may not have one yet, and a public avatar never
      // will. The image service stopped requiring it (zenitailab !859).
      const dataUrl = await generateImageDataUrl({ accountId, prompt })
      // Into the cropper, like an upload — the model's framing is not the last
      // word on which square of it is the avatar.
      setPendingImage(await dataUrlToFile(dataUrl))
      setShowPrompt(false)
      setPrompt("")
    } catch (err) {
      setGenerateError(err?.message || "Could not generate the image.")
    } finally {
      setGenerating(false)
    }
  }

  const content = (
    <div className="fixed inset-0 z-[9998] flex items-start justify-center overflow-y-auto bg-black/80 p-4 backdrop-blur-sm sm:items-center">
      {/* Not a form in a panel that happens to produce a card — the card itself,
          made in place. Everything an avatar needs to exist is filled in on it:
          the cover, the name, the description, who can see it and where it
          lives. Blocks, output and examples come afterwards, inside the avatar. */}
      <div className="relative my-auto w-full max-w-[340px] overflow-hidden rounded-2xl border border-white/15 bg-[#14141b] shadow-2xl">
        {/* The art sits inside the card, not bled to its edge — a frame around
            it, the way a trading card holds its illustration. */}
        <div className="p-4 pb-0">
        <div className="relative aspect-square w-full overflow-hidden rounded-lg border border-white/25 bg-black/40 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.6)]">
          {image ? (
            <img src={image} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="flex h-full w-full flex-col items-center justify-center gap-1.5 text-gray-600">
              <ImageIcon className="h-8 w-8" />
              <span className="text-xs">No cover image</span>
            </span>
          )}

          {generating && (
            <span className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-black/70 text-xs text-gray-300">
              <Loader className="h-5 w-5 animate-spin text-purple-300" />
              Generating… this takes a few seconds
            </span>
          )}

          {/* Upload or generate. Both end in the cropper, so however the image
              arrived, it is the user who says which square of it is the avatar. */}
          {!generating && (
            <div className="absolute inset-x-0 bottom-0 flex gap-2 bg-gradient-to-t from-black/85 to-transparent p-2 pt-8">
              <CoverAction Icon={Upload} label="Upload" onClick={() => fileRef.current?.click()} />
              <CoverAction
                Icon={Sparkles}
                label="Generate"
                active={showPrompt}
                onClick={() => setShowPrompt((v) => !v)}
              />
            </div>
          )}
        </div>
        </div>
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={pickFile} />

        {showPrompt && !generating && (
          <div className="space-y-2 border-b border-white/10 bg-black/30 p-3">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={2}
              autoFocus
              placeholder="A friendly robot mascot, flat vector, purple background…"
              className="w-full resize-none rounded-lg border border-white/15 bg-black/40 px-2.5 py-2 text-xs text-white placeholder:text-gray-600 focus:border-purple-500 focus:outline-none"
            />
            {generateError && <p className="text-xs text-red-400">{generateError}</p>}
            <button
              type="button"
              onClick={handleGenerate}
              disabled={!prompt.trim()}
              className="flex w-full items-center justify-center gap-1.5 rounded-lg bg-purple-600/90 py-2 text-xs font-medium text-white transition-colors hover:bg-purple-500 disabled:opacity-40"
            >
              <Sparkles className="h-3.5 w-3.5" />
              Generate image
            </button>
            {/* The account is charged whether or not the result is kept, so say
                so before the click, not in a toast afterwards. */}
            <p className="text-center text-[10px] text-gray-500">Uses coins from your balance.</p>
          </div>
        )}

        {/* Inside the art, clear of the frame — sitting on the frame's border
            reads as a misalignment rather than a button. */}
        <button
          type="button"
          onClick={onCancel}
          aria-label="Close"
          className="absolute right-6 top-6 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-gray-300 backdrop-blur-sm transition-colors hover:bg-black/80 hover:text-white"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="p-4">
          <Input
            autoFocus
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              if (e.target.value.trim()) clearValidationError("name")
            }}
            placeholder="Name your avatar *"
            className={`h-auto border-0 bg-transparent px-0 text-base font-semibold text-white placeholder:font-normal placeholder:text-gray-500 focus-visible:ring-0 ${
              nameMissing ? "text-red-300 placeholder:text-red-400/70" : ""
            }`}
          />
          {nameMissing && <p className="text-xs text-red-400">A name is required.</p>}

          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            placeholder="What is this avatar for? — optional"
            className="mt-1 w-full resize-none border-0 bg-transparent p-0 text-sm leading-snug text-gray-400 placeholder:text-gray-500 focus:outline-none"
          />

          {/* On the card, where the badge and the project end up being read. */}
          <div className="mt-4 space-y-2.5 border-t border-white/10 pt-4">
            <div className="grid grid-cols-2 gap-2">
              <VisibilityChip
                active={!isPublic}
                onClick={() => setIsPublic(false)}
                Icon={Lock}
                label="Private"
              />
              <VisibilityChip
                active={isPublic}
                onClick={() => setIsPublic(true)}
                Icon={Globe}
                label="Public"
              />
            </div>

            {!isPublic && (
              projectsError ? (
                <p className="text-xs text-red-400">{projectsError}</p>
              ) : projects.length === 0 ? (
                <p className="text-xs text-gray-500">No projects found.</p>
              ) : (
                <>
                  {/* One project, not many: an avatar carries a single project_id.
                      The old checkbox list let you tick several and quietly sent
                      the first. */}
                  <select
                    value={projectId}
                    onChange={(e) => {
                      setSelectedProjects(e.target.value ? [e.target.value] : [])
                      if (e.target.value) clearValidationError("projects")
                    }}
                    className={`w-full rounded-lg border bg-black/40 px-2.5 py-2 text-xs text-white focus:outline-none ${
                      projectMissing ? "border-red-400" : "border-white/15"
                    }`}
                  >
                    <option value="">Select a project…</option>
                    {projects.map((p) => (
                      <option key={p.project_id} value={p.project_id}>
                        {p.name} {p.organization_id ? "(Org)" : "(Personal)"}
                      </option>
                    ))}
                  </select>
                  {projectMissing && (
                    <p className="text-xs text-red-400">Pick the project this avatar belongs to.</p>
                  )}
                </>
              )
            )}
          </div>

          <button
            type="button"
            onClick={onSubmit}
            disabled={submitting}
            className="mt-4 flex w-full items-center justify-center gap-2 rounded-lg bg-primary py-2.5 text-sm font-medium text-white transition-colors hover:bg-purple-500 disabled:opacity-50"
          >
            {submitting && <Loader className="h-4 w-4 animate-spin" />}
            {submitLabel}
          </button>
          <p className="mt-2 text-center text-[11px] text-gray-500">
            Blocks, output and examples come next, inside the avatar.
          </p>
        </div>
      </div>

      {pendingImage && (
        <ImageCropper
          file={pendingImage}
          onCancel={() => setPendingImage(null)}
          onError={(msg) => {
            onImageError(msg)
            setPendingImage(null)
          }}
          onDone={(dataUrl) => {
            onImageChange(dataUrl)
            setPendingImage(null)
          }}
        />
      )}
    </div>
  )

  return typeof document === "undefined" ? content : createPortal(content, document.body)
}

function VisibilityChip({ active, onClick, Icon, label }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center justify-center gap-1.5 rounded-lg border py-2 text-xs transition-colors ${
        active
          ? "border-purple-500/50 bg-purple-600/20 text-purple-200"
          : "border-white/15 bg-white/5 text-gray-400 hover:text-white"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  )
}

function CoverAction({ Icon, label, onClick, active }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-1 items-center justify-center gap-1.5 rounded-lg border py-1.5 text-xs backdrop-blur-sm transition-colors ${
        active
          ? "border-purple-400/60 bg-purple-600/40 text-white"
          : "border-white/20 bg-black/50 text-gray-200 hover:bg-black/70 hover:text-white"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  )
}

/** The cropper works on a File, and a generated image arrives as a data URL. */
const dataUrlToFile = async (dataUrl) => {
  const blob = await (await fetch(dataUrl)).blob()
  return new File([blob], "generated.png", { type: blob.type || "image/png" })
}
